import {processTraceData} from '@github-ui/copilot-chat-state/tracing'
import type {CopilotChatOrg, SuccessfulAPIResult, TracePayload} from '@github-ui/copilot-chat-types'
import {copilotFeatureFlags} from '@github-ui/copilot-feature-flags'
import {isStaff} from '@github-ui/stats'

import {CopilotChatService} from '../../../utils/copilot-chat-service'
import type {TraceAction, TraceState} from './trace-reducer'
import {safeTimestamp} from './trace-reducer'

export class TraceManager {
  dispatch: React.Dispatch<TraceAction>
  service: CopilotChatService
  private getTraceState: () => TraceState

  constructor(
    dispatch: React.Dispatch<TraceAction>,
    getTraceState: () => TraceState,
    apiURL: string,
    ssoOrgs: CopilotChatOrg[],
  ) {
    this.dispatch = dispatch
    this.service = new CopilotChatService(apiURL, ssoOrgs)
    this.getTraceState = getTraceState
  }

  showTraceDisplay = async (value: boolean, threadId?: string | null) => {
    if (value && threadId) {
      await this.fetchAndLoadTraces(threadId)
    }

    this.dispatch({type: 'SET_TRACE_INFO', traceInfo: {visible: value, threadId: threadId ?? undefined}})
  }

  // Fetches traces for a given thread ID and loads them into state if any exist
  async fetchAndLoadTraces(threadId: string, sharedId?: string): Promise<void> {
    // Check if user has tracing enabled and is staff
    if (!copilotFeatureFlags.requestTracing || !isStaff()) {
      return
    }
    const state = this.getTraceState()

    // Check if we've already loaded traces for this thread
    if (state.traceInfo?.loadedThreadIds?.has(threadId)) {
      return
    }

    try {
      const result = await this.service.fetchTracesByThreadID(threadId, sharedId)
      if (result.ok && result.payload.length > 0) {
        this.addTracesToState(state, result, threadId)
      } else {
        // Mark as loaded even if no traces found to avoid repeated calls
        this.dispatch({
          type: 'SET_TRACE_INFO',
          traceInfo: {threadId},
        })
      }
    } catch {
      /* empty */
    }
  }

  private addTracesToState(state: TraceState, result: SuccessfulAPIResult<TracePayload[]>, threadId: string) {
    const currentTraces = new Map(state.traceInfo?.traces || [])

    // Handles processing each trace in thread
    for (const trace of result.payload) {
      const rootNodes = processTraceData(trace.spans)

      // For each root node, retain the ISOString format we currently use
      // use timestamp with threadID to create unique key (prevents issue with sharing traces)
      for (const [, rootNode] of rootNodes.entries()) {
        const timestamp = safeTimestamp(rootNode.startTime)
        if (!timestamp) continue
        const messageId = `${threadId}:${timestamp}`
        currentTraces.set(messageId, {
          spanNode: rootNode,
          threadId,
          actualTraceId: trace.id,
          timestamp,
          requestId: trace.requestId,
        })
      }
    }

    this.dispatch({
      type: 'SET_TRACE_INFO',
      traceInfo: {
        traces: currentTraces,
        threadId,
      },
    })
  }

  async deleteTrace(threadID: string, traceID: string) {
    const res = await this.service.deleteTrace(threadID, traceID)
    if (res.ok) {
      this.dispatch({type: 'REMOVE_TRACE', traceID})
    }
    return res
  }
}
